<template>
    <div
        class="custom-field-config"
        :class="{
            'custom-field-config--expanded': isExpanded,
            'custom-field-config--invalid-condition': hasInvalidVisibility
        }"
    >
        <div class="custom-field-config__header">
            <button
                type="button"
                class="custom-field-config__toggle"
                :aria-expanded="isExpanded ? 'true' : 'false'"
                :title="isExpanded ? 'Свернуть' : 'Развернуть'"
                @click="toggleExpanded"
            >
                <i :class="isExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
            </button>

            <div class="custom-field-config__number">{{ displayIndex }}</div>

            <div class="custom-field-config__summary" @click="toggleExpanded">
                <div class="custom-field-config__title-row">
                    <strong>{{ displayLabel }}</strong>
                    <b-badge variant="light" class="custom-field-config__badge">{{ fieldTypeLabel }}</b-badge>
                    <b-badge v-if="localField.required" variant="danger" class="custom-field-config__badge">
                        Обязательное
                    </b-badge>
                    <b-badge
                        v-if="visibilityEnabled"
                        :variant="hasInvalidVisibility ? 'warning' : 'info'"
                        class="custom-field-config__badge"
                    >
                        Условие
                    </b-badge>
                </div>
                <div class="custom-field-config__meta">
                    <span>{{ displayKey }}</span>
                    <span v-if="fieldMeta">{{ fieldMeta }}</span>
                </div>
            </div>

            <div class="custom-field-config__actions">
                <b-button-group size="sm">
                    <b-button
                        variant="outline-secondary"
                        :disabled="isFirst"
                        title="Выше"
                        @click.stop="$emit('move-up')"
                    >
                        <i class="fas fa-chevron-up"></i>
                    </b-button>
                    <b-button
                        variant="outline-secondary"
                        :disabled="isLast"
                        title="Ниже"
                        @click.stop="$emit('move-down')"
                    >
                        <i class="fas fa-chevron-down"></i>
                    </b-button>
                </b-button-group>
                <b-button
                    variant="outline-danger"
                    size="sm"
                    title="Удалить"
                    @click.stop="$emit('remove')"
                >
                    <i class="fas fa-trash-alt"></i>
                </b-button>
            </div>
        </div>

        <div v-if="isExpanded" class="custom-field-config__body">
            <div class="custom-field-config__section">
                <div class="custom-field-config__section-title">Основное</div>
                <b-row>
                    <b-col md="5">
                        <b-form-group label="Название поля *" :label-for="getControlId('label')" class="mb-2">
                            <b-form-input
                                :id="getControlId('label')"
                                :value="localField.label"
                                placeholder="Например: Материалы"
                                @input="onLabelInput"
                                size="sm"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="4">
                        <b-form-group label="Системное имя *" :label-for="getControlId('key')" class="mb-2">
                            <b-input-group size="sm">
                                <b-form-input
                                    :id="getControlId('key')"
                                    :value="localField.key"
                                    placeholder="materials"
                                    :state="keyInputState"
                                    @input="onKeyInput"
                                />
                                <b-input-group-append>
                                    <b-button
                                        variant="outline-secondary"
                                        title="Сгенерировать из названия"
                                        @click="regenerateKey"
                                    >
                                        <i class="fas fa-magic"></i>
                                    </b-button>
                                </b-input-group-append>
                            </b-input-group>
                            <small :class="keyHintClass">{{ keyHintText }}</small>
                        </b-form-group>
                    </b-col>
                    <b-col md="3">
                        <b-form-group label="Тип поля *" :label-for="getControlId('type')" class="mb-2">
                            <v-select
                                :id="getControlId('type')"
                                v-model="localField.type"
                                :options="fieldTypeOptions"
                                label="text"
                                :reduce="option => option.value"
                                placeholder="Тип"
                                :clearable="false"
                                @input="onTypeChange"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col md="5" v-if="showReferenceSelect">
                        <b-form-group label="Справочник *" :label-for="getControlId('reference')" class="mb-2">
                            <v-select
                                :id="getControlId('reference')"
                                v-model="localField.reference"
                                :options="referenceOptions"
                                label="text"
                                :reduce="option => option.value"
                                placeholder="Справочник"
                                :clearable="false"
                                @input="emitUpdate"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col :md="showReferenceSelect ? 7 : 12">
                        <b-form-group label="Подсказка" :label-for="getControlId('placeholder')" class="mb-2">
                            <b-form-input
                                :id="getControlId('placeholder')"
                                v-model="localField.placeholder"
                                placeholder="Текст подсказки для пользователя"
                                @input="emitUpdate"
                                size="sm"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>

                <div class="custom-field-config__switches">
                    <b-form-checkbox
                        v-model="localField.required"
                        @change="emitUpdate"
                        switch
                        size="sm"
                    >
                        Обязательное
                    </b-form-checkbox>
                    <b-form-checkbox
                        v-if="localField.type === 'reference_multiple'"
                        v-model="localField.allow_quantities"
                        @change="emitUpdate"
                        switch
                        size="sm"
                    >
                        Указывать количество
                    </b-form-checkbox>
                    <b-form-checkbox
                        v-if="showReferenceSelect"
                        v-model="localField.allow_manual_entries"
                        @change="emitUpdate"
                        switch
                        size="sm"
                    >
                        Ручной ввод
                    </b-form-checkbox>
                    <b-form-checkbox
                        v-if="showObjectFilter"
                        v-model="localField.filter_by_object"
                        @change="emitUpdate"
                        switch
                        size="sm"
                    >
                        Фильтр по объекту
                    </b-form-checkbox>
                </div>
            </div>

            <div v-if="localField.type === 'number'" class="custom-field-config__section">
                <div class="custom-field-config__section-title">Число</div>
                <b-row>
                    <b-col md="4">
                        <b-form-group label="Минимум" :label-for="getControlId('min')" class="mb-2">
                            <b-form-input
                                :id="getControlId('min')"
                                v-model.number="localField.min"
                                type="number"
                                @input="emitUpdate"
                                size="sm"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="4">
                        <b-form-group label="Максимум" :label-for="getControlId('max')" class="mb-2">
                            <b-form-input
                                :id="getControlId('max')"
                                v-model.number="localField.max"
                                type="number"
                                @input="emitUpdate"
                                size="sm"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="4">
                        <b-form-group label="Шаг" :label-for="getControlId('step')" class="mb-2">
                            <b-form-input
                                :id="getControlId('step')"
                                v-model.number="localField.step"
                                type="number"
                                @input="emitUpdate"
                                size="sm"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
            </div>

            <div v-if="localField.type === 'text' || localField.type === 'textarea'" class="custom-field-config__section">
                <div class="custom-field-config__section-title">Текст</div>
                <b-row>
                    <b-col md="4">
                        <b-form-group label="Максимальная длина" :label-for="getControlId('maxlength')" class="mb-2">
                            <b-form-input
                                :id="getControlId('maxlength')"
                                v-model.number="localField.maxlength"
                                type="number"
                                @input="emitUpdate"
                                size="sm"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
            </div>

            <div v-if="showDropdownOptions" class="custom-field-config__section">
                <div class="custom-field-config__section-heading">
                    <div class="custom-field-config__section-title">Варианты списка</div>
                    <b-button
                        variant="outline-primary"
                        size="sm"
                        @click="addDropdownOption"
                    >
                        <i class="fas fa-plus"></i>
                        Добавить
                    </b-button>
                </div>

                <div
                    v-for="(option, optionIndex) in localField.options"
                    :key="`dropdown-option-${optionIndex}`"
                    class="custom-field-config__option-row"
                >
                    <div class="custom-field-config__option-number">{{ optionIndex + 1 }}</div>
                    <b-form-input
                        v-model="localField.options[optionIndex].label"
                        placeholder="Текст"
                        size="sm"
                        @input="onDropdownOptionInput"
                    />
                    <b-form-input
                        v-model="localField.options[optionIndex].value"
                        placeholder="Код"
                        size="sm"
                        @input="onDropdownOptionInput"
                    />
                    <b-button
                        variant="outline-danger"
                        size="sm"
                        title="Удалить вариант"
                        @click="removeDropdownOption(optionIndex)"
                    >
                        <i class="fas fa-times"></i>
                    </b-button>
                </div>
            </div>

            <div class="custom-field-config__section custom-field-config__section--condition">
                <div class="custom-field-config__condition-toggle">
                    <b-form-checkbox
                        :checked="visibilityEnabled"
                        :disabled="availableVisibilityFieldOptions.length === 0 && !visibilityEnabled"
                        switch
                        size="sm"
                        @change="onVisibilityEnabledChange"
                    >
                        Условие показа
                    </b-form-checkbox>
                    <b-badge v-if="hasInvalidVisibility" variant="warning">
                        Недоступное поле-источник
                    </b-badge>
                </div>

                <div v-if="visibilityEnabled" class="custom-field-config__condition-body">
                    <b-row>
                        <b-col md="4">
                            <b-form-group label="Зависит от поля *" :label-for="getControlId('visibility-source')" class="mb-2">
                                <v-select
                                    :id="getControlId('visibility-source')"
                                    v-model="localField.visibility.field_key"
                                    :options="availableVisibilityFieldOptions"
                                    label="text"
                                    :reduce="option => option.value"
                                    placeholder="Поле"
                                    :clearable="false"
                                    @input="onVisibilitySourceChange"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col md="4">
                            <b-form-group label="Условие *" :label-for="getControlId('visibility-operator')" class="mb-2">
                                <b-form-select
                                    :id="getControlId('visibility-operator')"
                                    v-model="localField.visibility.operator"
                                    :options="visibilityOperatorOptions"
                                    size="sm"
                                    @change="onVisibilityOperatorChange"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col md="4" v-if="showVisibilityValue">
                            <b-form-group label="Значение *" :label-for="getControlId('visibility-value')" class="mb-2">
                                <v-select
                                    v-if="showVisibilityValueSelect"
                                    :id="getControlId('visibility-value')"
                                    v-model="visibilityValueModel"
                                    :options="visibilityValueOptions"
                                    :multiple="isVisibilityMultiValue"
                                    label="text"
                                    :reduce="option => option.value"
                                    placeholder="Значение"
                                    :clearable="true"
                                />
                                <b-form-input
                                    v-else
                                    :id="getControlId('visibility-value')"
                                    v-model="localField.visibility.value"
                                    placeholder="ID или текст"
                                    @input="emitUpdate"
                                    size="sm"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
function isDropdownFieldType(type) {
    return type === 'select_single' || type === 'select_multiple';
}

const TRANSLIT_MAP = {
    а: 'a',
    б: 'b',
    в: 'v',
    г: 'g',
    д: 'd',
    е: 'e',
    ё: 'e',
    ж: 'zh',
    з: 'z',
    и: 'i',
    й: 'y',
    к: 'k',
    л: 'l',
    м: 'm',
    н: 'n',
    о: 'o',
    п: 'p',
    р: 'r',
    с: 's',
    т: 't',
    у: 'u',
    ф: 'f',
    х: 'h',
    ц: 'ts',
    ч: 'ch',
    ш: 'sh',
    щ: 'sch',
    ъ: '',
    ы: 'y',
    ь: '',
    э: 'e',
    ю: 'yu',
    я: 'ya',
    і: 'i',
    ў: 'u'
};

function transliterate(value) {
    return Array.from(String(value || '').toLowerCase())
        .map(char => Object.prototype.hasOwnProperty.call(TRANSLIT_MAP, char) ? TRANSLIT_MAP[char] : char)
        .join('');
}

function normalizeSystemKey(value) {
    const key = transliterate(value)
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');

    if (!key) {
        return 'field';
    }
    return /^[a-z]/.test(key) ? key : `field_${key}`;
}

function normalizeReservedKey(value) {
    return String(value || '').trim().toLowerCase();
}

function makeUniqueSystemKey(baseKey, reservedKeys) {
    const base = normalizeSystemKey(baseKey);
    const used = new Set((Array.isArray(reservedKeys) ? reservedKeys : [])
        .map(normalizeReservedKey)
        .filter(Boolean));
    let candidate = base;
    let suffix = 2;

    while (used.has(candidate.toLowerCase())) {
        candidate = `${base}_${suffix}`;
        suffix += 1;
    }

    return candidate;
}

function normalizeDropdownOption(option) {
    if (option === null || option === undefined || option === '') {
        return { value: '', label: '' };
    }
    if (typeof option === 'string' || typeof option === 'number') {
        return {
            value: String(option).trim(),
            label: String(option).trim()
        };
    }
    if (typeof option !== 'object' || Array.isArray(option)) {
        return { value: '', label: '' };
    }

    const rawValue = option.value !== undefined
        ? option.value
        : (option.id !== undefined ? option.id : option.key);
    const rawLabel = [option.label, option.text, option.name, rawValue].find(item =>
        item !== undefined && item !== null && String(item).trim() !== ''
    );
    return {
        value: rawValue === undefined || rawValue === null ? '' : String(rawValue).trim(),
        label: String(rawLabel || '').trim()
    };
}

function createDropdownOption() {
    return { value: '', label: '' };
}

function splitVisibilityValue(value) {
    if (Array.isArray(value)) {
        return value.filter(item => item !== undefined && item !== null && String(item).trim() !== '');
    }
    if (value === undefined || value === null || value === '') {
        return [];
    }
    return String(value)
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);
}

function normalizeLocalField(field) {
    const next = { ...(field || {}) };
    const visibility = next.visibility && typeof next.visibility === 'object'
        ? next.visibility
        : {};
    next.visibility = {
        enabled: visibility.enabled === true,
        field_key: visibility.field_key || visibility.fieldKey || '',
        operator: visibility.operator || 'equals',
        value: visibility.value !== undefined ? visibility.value : ''
    };
    if (isDropdownFieldType(next.type)) {
        const options = Array.isArray(next.options) ? next.options : [];
        next.options = options.length
            ? options.map(option => normalizeDropdownOption(option))
            : [createDropdownOption()];
    }
    return next;
}

export default {
    name: "CustomFieldConfig",
    props: {
        value: {
            type: Object,
            required: true
        },
        index: {
            type: Number,
            default: 0
        },
        isFirst: {
            type: Boolean,
            default: false
        },
        isLast: {
            type: Boolean,
            default: false
        },
        availableFields: {
            type: Array,
            default: () => []
        },
        reservedKeys: {
            type: Array,
            default: () => []
        }
    },
    data() {
        const initialField = normalizeLocalField(this.value);
        const initialKey = String(initialField.key || '').trim();
        return {
            localField: initialField,
            isExpanded: !(initialField.label && initialField.key && initialField.type),
            keyManuallyEdited: Boolean(initialKey),
            lastGeneratedKey: initialKey,
            fieldTypeOptions: [
                { value: 'text', text: 'Текстовое поле' },
                { value: 'textarea', text: 'Многострочное текстовое поле' },
                { value: 'number', text: 'Числовое поле' },
                { value: 'date', text: 'Дата' },
                { value: 'date_range', text: 'Дата с-по' },
                { value: 'datetime', text: 'Дата и время' },
                { value: 'datetime_range', text: 'Дата и время с-по' },
                { value: 'checkbox', text: 'Флажок (да/нет)' },
                { value: 'select_single', text: 'Выбор из выпадающего списка (один)' },
                { value: 'select_multiple', text: 'Выбор из выпадающего списка (множественный)' },
                { value: 'reference_single', text: 'Выбор из справочника (один)' },
                { value: 'reference_multiple', text: 'Выбор из справочника (множественный)' }
            ],
            referenceOptions: [
                { value: 'materials', text: 'Материалы' },
                { value: 'material_groups', text: 'Группы материалов' },
                { value: 'users', text: 'Пользователи' },
                { value: 'departments', text: 'Отделы' },
                { value: 'objects', text: 'Строительные объекты' },
                { value: 'objects_in_process', text: 'Объекты в работе' },
                { value: 'work_types', text: 'Виды работ' },
                { value: 'work_groups', text: 'Рабочие группы' }
            ],
            visibilityOperatorOptions: [
                { value: 'equals', text: 'равно значению' },
                { value: 'not_equals', text: 'не равно значению' },
                { value: 'filled', text: 'заполнено' },
                { value: 'empty', text: 'не заполнено' },
                { value: 'in', text: 'одно из значений' },
                { value: 'not_in', text: 'не одно из значений' }
            ]
        }
    },
    computed: {
        displayIndex() {
            return this.index + 1;
        },
        displayLabel() {
            return String(this.localField.label || '').trim() || 'Новое поле';
        },
        displayKey() {
            const key = String(this.localField.key || '').trim();
            return key ? `key: ${key}` : 'key не задан';
        },
        fieldTypeLabel() {
            const type = String(this.localField.type || '').trim();
            const option = this.fieldTypeOptions.find(item => item.value === type);
            return option ? option.text : 'Тип не выбран';
        },
        referenceLabel() {
            const option = this.referenceOptions.find(item => item.value === this.localField.reference);
            return option ? option.text : '';
        },
        dropdownOptionsCount() {
            return Array.isArray(this.localField.options)
                ? this.localField.options.filter(option => normalizeDropdownOption(option).label).length
                : 0;
        },
        fieldMeta() {
            if (this.showReferenceSelect && this.referenceLabel) {
                return this.referenceLabel;
            }
            if (this.showDropdownOptions) {
                return `${this.dropdownOptionsCount} вариантов`;
            }
            if (this.localField.maxlength) {
                return `до ${this.localField.maxlength} символов`;
            }
            return '';
        },
        normalizedCurrentKey() {
            return normalizeReservedKey(this.localField.key);
        },
        isKeyDuplicated() {
            return Boolean(this.normalizedCurrentKey) &&
                (Array.isArray(this.reservedKeys) ? this.reservedKeys : [])
                    .map(normalizeReservedKey)
                    .filter(Boolean)
                    .indexOf(this.normalizedCurrentKey) !== -1;
        },
        keyInputState() {
            return this.isKeyDuplicated ? false : null;
        },
        keyHintClass() {
            return this.isKeyDuplicated ? 'text-danger' : 'text-muted';
        },
        keyHintText() {
            if (this.isKeyDuplicated) {
                return 'Такое системное имя уже используется';
            }
            return 'Заполняется автоматически из названия';
        },
        showReferenceSelect() {
            return this.localField.type === 'reference_single' || 
                   this.localField.type === 'reference_multiple';
        },
        showDropdownOptions() {
            return isDropdownFieldType(this.localField.type);
        },
        showObjectFilter() {
            return this.showReferenceSelect && 
                   ['materials', 'work_types', 'users'].includes(this.localField.reference);
        },
        availableVisibilityFieldOptions() {
            return (Array.isArray(this.availableFields) ? this.availableFields : [])
                .filter(field => field && field.key && field.label)
                .map(field => ({
                    value: field.key,
                    text: `${field.label} (${field.key})`
                }));
        },
        selectedVisibilityField() {
            const key = this.localField.visibility && this.localField.visibility.field_key;
            return (Array.isArray(this.availableFields) ? this.availableFields : [])
                .find(field => field && String(field.key || '') === String(key || '')) || null;
        },
        visibilityEnabled() {
            return this.localField.visibility && this.localField.visibility.enabled === true;
        },
        hasInvalidVisibility() {
            return this.visibilityEnabled &&
                (!this.localField.visibility.field_key || !this.selectedVisibilityField);
        },
        showVisibilityValue() {
            const operator = this.localField.visibility && this.localField.visibility.operator;
            return operator !== 'filled' && operator !== 'empty';
        },
        isVisibilityMultiValue() {
            const operator = this.localField.visibility && this.localField.visibility.operator;
            return operator === 'in' || operator === 'not_in';
        },
        visibilityValueOptions() {
            const sourceField = this.selectedVisibilityField;
            if (!sourceField) {
                return [];
            }
            if (sourceField.type === 'checkbox') {
                return [
                    { value: 'true', text: 'Да' },
                    { value: 'false', text: 'Нет' }
                ];
            }
            if (isDropdownFieldType(sourceField.type) || Array.isArray(sourceField.options)) {
                const options = Array.isArray(sourceField.options) ? sourceField.options : [];
                return options
                    .map(option => normalizeDropdownOption(option))
                    .filter(option => option.label)
                    .map(option => ({
                        value: option.value === undefined || option.value === null || String(option.value).trim() === ''
                            ? option.label
                            : option.value,
                        text: option.label
                    }));
            }
            return [];
        },
        showVisibilityValueSelect() {
            return this.showVisibilityValue && this.visibilityValueOptions.length > 0;
        },
        visibilityValueModel: {
            get() {
                const value = this.localField.visibility ? this.localField.visibility.value : '';
                if (this.isVisibilityMultiValue) {
                    return splitVisibilityValue(value);
                }
                if (Array.isArray(value)) {
                    return value[0] || '';
                }
                return value === undefined || value === null ? '' : value;
            },
            set(value) {
                if (!this.localField.visibility) {
                    return;
                }
                if (this.isVisibilityMultiValue) {
                    this.localField.visibility.value = Array.isArray(value)
                        ? value
                        : (value ? [value] : []);
                } else {
                    this.localField.visibility.value = Array.isArray(value)
                        ? (value[0] || '')
                        : (value || '');
                }
                this.emitUpdate();
            }
        }
    },
    watch: {
        value: {
            handler(newVal) {
                this.localField = normalizeLocalField(newVal);
            },
            deep: true
        }
    },
    methods: {
        toggleExpanded() {
            this.isExpanded = !this.isExpanded;
        },
        getControlId(name) {
            const key = this.localField._uid || this.localField.key || this.index;
            return `custom-field-${String(key).replace(/[^a-zA-Z0-9_-]/g, '-')}-${name}`;
        },
        shouldAutoGenerateKey() {
            const currentKey = String(this.localField.key || '').trim();
            return !currentKey || !this.keyManuallyEdited || currentKey === this.lastGeneratedKey;
        },
        generateKeyFromLabel() {
            return makeUniqueSystemKey(this.localField.label || 'field', this.reservedKeys);
        },
        applyGeneratedKey() {
            const key = this.generateKeyFromLabel();
            this.localField.key = key;
            this.lastGeneratedKey = key;
            this.keyManuallyEdited = false;
        },
        onLabelInput(value) {
            this.localField.label = value;
            if (this.shouldAutoGenerateKey()) {
                this.applyGeneratedKey();
            }
            this.emitUpdate();
        },
        onKeyInput(value) {
            const rawValue = String(value || '').trim();
            this.localField.key = rawValue ? normalizeSystemKey(rawValue) : '';
            this.keyManuallyEdited = Boolean(String(this.localField.key || '').trim());
            this.emitUpdate();
        },
        regenerateKey() {
            this.applyGeneratedKey();
            this.emitUpdate();
        },
        onTypeChange() {
            // Сброс специфичных полей при смене типа
            if (!this.showReferenceSelect) {
                delete this.localField.reference;
                delete this.localField.allow_quantities;
                delete this.localField.filter_by_object;
                delete this.localField.allow_manual_entries;
            }
            if (!this.showDropdownOptions) {
                delete this.localField.options;
            } else {
                this.ensureDropdownOptions();
            }
            if (this.localField.type !== 'number') {
                delete this.localField.min;
                delete this.localField.max;
                delete this.localField.step;
            }
            if (this.localField.type !== 'text' && this.localField.type !== 'textarea') {
                delete this.localField.maxlength;
            }
            this.emitUpdate();
        },
        ensureDropdownOptions() {
            if (!Array.isArray(this.localField.options) || !this.localField.options.length) {
                this.$set(this.localField, 'options', [createDropdownOption()]);
                return;
            }
            this.localField.options = this.localField.options.map(option => normalizeDropdownOption(option));
        },
        addDropdownOption() {
            this.ensureDropdownOptions();
            this.localField.options.push(createDropdownOption());
            this.emitUpdate();
        },
        removeDropdownOption(index) {
            if (!Array.isArray(this.localField.options)) {
                this.$set(this.localField, 'options', [createDropdownOption()]);
                this.emitUpdate();
                return;
            }
            this.localField.options.splice(index, 1);
            if (!this.localField.options.length) {
                this.localField.options.push(createDropdownOption());
            }
            this.emitUpdate();
        },
        onDropdownOptionInput() {
            this.emitUpdate();
        },
        onVisibilitySourceChange() {
            if (this.localField.visibility) {
                this.localField.visibility.value = this.isVisibilityMultiValue ? [] : '';
            }
            this.emitUpdate();
        },
        onVisibilityEnabledChange(value) {
            this.localField.visibility = {
                ...(this.localField.visibility || {}),
                enabled: value === true,
                operator: (this.localField.visibility && this.localField.visibility.operator) || 'equals',
                field_key: (this.localField.visibility && this.localField.visibility.field_key) || '',
                value: (this.localField.visibility && this.localField.visibility.value) || ''
            };
            if (!this.localField.visibility.enabled) {
                this.localField.visibility.field_key = '';
                this.localField.visibility.value = '';
            }
            this.emitUpdate();
        },
        onVisibilityOperatorChange() {
            if (!this.showVisibilityValue && this.localField.visibility) {
                this.localField.visibility.value = '';
            } else if (this.localField.visibility) {
                if (this.isVisibilityMultiValue) {
                    this.localField.visibility.value = splitVisibilityValue(this.localField.visibility.value);
                } else if (Array.isArray(this.localField.visibility.value)) {
                    this.localField.visibility.value = this.localField.visibility.value[0] || '';
                }
            }
            this.emitUpdate();
        },
        emitUpdate() {
            this.$emit('input', { ...this.localField });
        }
    }
}
</script>

<style scoped>
.custom-field-config {
    border: 1px solid #d8dee4;
    border-radius: 6px;
    background: #ffffff;
    margin-bottom: 0.75rem;
}

.custom-field-config--expanded {
    border-color: #b8c2cc;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.custom-field-config--invalid-condition {
    border-color: #f0ad4e;
}

.custom-field-config__header {
    display: grid;
    grid-template-columns: auto auto minmax(0, 1fr) auto;
    gap: 0.75rem;
    align-items: center;
    padding: 0.65rem 0.75rem;
}

.custom-field-config__toggle {
    width: 32px;
    height: 32px;
    padding: 0;
    border: 1px solid #d8dee4;
    border-radius: 4px;
    background: #f8f9fa;
    color: #495057;
}

.custom-field-config__number {
    min-width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #edf2f7;
    color: #495057;
    font-weight: 600;
    font-size: 0.85rem;
}

.custom-field-config__summary {
    min-width: 0;
    cursor: pointer;
}

.custom-field-config__title-row,
.custom-field-config__meta,
.custom-field-config__actions,
.custom-field-config__section-heading,
.custom-field-config__condition-toggle,
.custom-field-config__switches {
    display: flex;
    align-items: center;
}

.custom-field-config__title-row {
    gap: 0.4rem;
    flex-wrap: wrap;
}

.custom-field-config__title-row strong {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.custom-field-config__badge {
    font-weight: 500;
}

.custom-field-config__meta {
    gap: 0.75rem;
    color: #6c757d;
    font-size: 0.82rem;
    margin-top: 0.15rem;
}

.custom-field-config__actions {
    gap: 0.5rem;
}

.custom-field-config__body {
    border-top: 1px solid #e9ecef;
    padding: 0.75rem;
    background: #fbfcfd;
}

.custom-field-config__section {
    padding: 0.75rem;
    border: 1px solid #e1e7ec;
    border-radius: 6px;
    background: #ffffff;
    margin-bottom: 0.75rem;
}

.custom-field-config__section:last-child {
    margin-bottom: 0;
}

.custom-field-config__section-title {
    color: #495057;
    font-weight: 600;
    font-size: 0.84rem;
    margin-bottom: 0.6rem;
}

.custom-field-config__section-heading {
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.6rem;
}

.custom-field-config__section-heading .custom-field-config__section-title {
    margin-bottom: 0;
}

.custom-field-config__switches {
    flex-wrap: wrap;
    gap: 0.75rem 1.25rem;
    padding-top: 0.25rem;
}

.custom-field-config__condition-toggle {
    justify-content: space-between;
    gap: 0.75rem;
}

.custom-field-config__condition-body {
    margin-top: 0.75rem;
}

.custom-field-config__option-row {
    display: grid;
    grid-template-columns: 32px minmax(0, 1.2fr) minmax(0, 0.8fr) auto;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    align-items: start;
}

.custom-field-config__option-number {
    height: 31px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    background: #f1f3f5;
    color: #6c757d;
    font-size: 0.8rem;
}

.custom-field-config >>> .form-group,
.custom-field-config >>> .form-group input[type="text"],
.custom-field-config >>> .form-group input[type="number"],
.custom-field-config >>> .form-group textarea,
.custom-field-config >>> .form-group .v-select {
    width: 100%;
}

.custom-field-config >>> .v-select {
    min-height: 38px;
}

.custom-field-config >>> .v-select .vs__dropdown-toggle {
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    min-height: 38px;
}

.custom-field-config >>> .v-select .vs__dropdown-toggle:focus-within {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.custom-field-config >>> .v-select .vs__search {
    margin: 0;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
}

.custom-field-config >>> .v-select .vs__selected-options {
    padding: 0.375rem 0.75rem;
}

@media (max-width: 768px) {
    .custom-field-config__header {
        grid-template-columns: auto minmax(0, 1fr) auto;
    }

    .custom-field-config__number {
        display: none;
    }

    .custom-field-config__actions {
        flex-direction: column;
        align-items: stretch;
    }

    .custom-field-config__option-row {
        grid-template-columns: 28px minmax(0, 1fr) auto;
    }

    .custom-field-config__option-row input:nth-of-type(2) {
        grid-column: 2 / 4;
    }
}
</style>
